import { positiveQuantityOrNull } from '@paragon/client-tools';
import { ComponentDesignUploadFile } from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';
import { enqueueError, enqueueSuccess } from '@paragon/notification-tools';
import { addSpinnerMessage, removeSpinnerMessage } from '@paragon/task-spinner';
import { forceArray } from '@paragon/utilities';

import { stationsSlice } from '../store/stationsSlice';
import { uploadComponentDesigns } from './componentDesignActions';
import { fullyLoadStation } from './supervisorActions';
import { AsyncThunkActionVoid } from './thunkActions';

export function uploadComponentDesignsToStation(
  stationGuid: string,
  uploadFiles: ComponentDesignUploadFile[],
): AsyncThunkActionVoid {
  return async function uploadComponentDesignsToStationThunkAction(dispatch) {
    const spinnerId = dispatch(addSpinnerMessage('Uploading component designs...'));

    try {
      const uploadFilesResult = await dispatch(uploadComponentDesigns(uploadFiles));
      if (!uploadFilesResult) {
        dispatch(enqueueError('Unable to upload component designs.'));
        return;
      }

      await Promise.all(
        uploadFilesResult.componentDesignsWithTimestamps.map(({ componentDesign }) =>
          designService.stations.addComponentDesignGuidToStation(
            stationGuid,
            componentDesign.guid,
            positiveQuantityOrNull(uploadFilesResult.quantitiesFromFiles[componentDesign.guid]),
          ),
        ),
      );

      dispatch(enqueueSuccess('Component designs uploaded successfully.'));

      await dispatch(fullyLoadStation(stationGuid));
    } catch (error) {
      console.error(error);
      dispatch(enqueueError('Unable to upload component designs.'));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}

export function removeComponentDesignsFromStation(
  stationGuid: string,
  componentDesignGuids: string | string[],
): AsyncThunkActionVoid {
  return async function removeComponentDesignFromStationThunkAction(dispatch) {
    componentDesignGuids = forceArray(componentDesignGuids);
    const term = componentDesignGuids.length > 1 ? 'designs' : 'design';

    const spinnerId = dispatch(addSpinnerMessage(`Removing ${term}...`));

    try {
      const syncResult = await designService.stations.removeComponentDesignGuidsFromStation(
        stationGuid,
        componentDesignGuids,
      );

      dispatch(stationsSlice.actions.loadedFromServer({ stations: [syncResult.result], shallow: false }));
    } catch (error) {
      console.error(error);
      dispatch(enqueueError(`Unable to remove ${term} from station.`));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}

export function updateComponentDesignQuantityInStation(
  stationGuid: string,
  componentDesignGuid: string,
  quantity: number,
): AsyncThunkActionVoid {
  return async function updateComponentDesignQuantityThunkAction(dispatch) {
    const spinnerId = dispatch(addSpinnerMessage(`Updating quantity...`));

    try {
      const syncResult = await designService.stations.updateComponentDesignQuantity(
        stationGuid,
        componentDesignGuid,
        quantity,
      );

      dispatch(stationsSlice.actions.loadedFromServer({ stations: [syncResult.result], shallow: false }));
    } catch (error) {
      console.error(error);
      dispatch(enqueueError(`Unable to update quantity.`));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}
